import {FC, useEffect, useMemo, useState} from 'react'
import {AutoComplete, Button, Card, Dropdown, Flex, Input, Modal, Tag} from "antd";
import {ID, WordDto, WordList} from "../../../../../shared/types/api";
import '../../../../../styles/scss/word-item/worditem.scss'
import TruncatedTag from "../../../../blocks/tooltip/TruncatedTag";
import {useAppDispatch, useAppSelector} from "../../../../../store/hooks";
import {setWordsRoutine, wordsDataSelector} from "../../../../../store/words";
import {premiumStatusSelector} from "../../../../../store/global";
import WordUtils from "../../../../utils/WordUtils";
import {createNewWord, deleteWord, updateWord} from "../../../../../store/words/extraReducers";
import {translateCardText} from "../../../../../shared/http/textTranslator";
import * as API_WORD from "../../../../../shared/http/word";
import {debounce} from "../../../../utils/debounce";
import ConfirmModalDialog from "../../../../modalwindows/ConfirmModalDialog";
import {EditOutlined} from "@ant-design/icons";

interface WordItemV2Props {
    cardList: WordList,
    wordItem: WordDto,
    canEditWord: boolean,
    loadWords: (id: number) => void
}

const WordItemV2: FC<WordItemV2Props> = ({cardList, canEditWord, loadWords, wordItem}) => {

    const {id, rusWord, engWord, editMode, loading} = wordItem

    const words = useAppSelector(wordsDataSelector)
    const dispatch = useAppDispatch()
    const setWordsRedux = (w: any) => dispatch(setWordsRoutine(w))
    const isPremium = useAppSelector(premiumStatusSelector)
    const {sideOneLang, sideTwoLang} = cardList

    const [validRus, setValidRus] = useState<boolean>(true)
    const [validEng, setValidEng] = useState<boolean>(true)

    const [sideTwoOptions, setSideTwoOptions] = useState([]);
    const [isOpenSideTwo, setIsOpenSideTwo] = useState(false);

    const [sideOneOptions, setSideOneOptions] = useState([]);
    const [isOpenSideOne, setIsOpenSideOne] = useState(false);

    const [firstTranslateValue, setFirstTranslateValue] = useState(null)
    const [secondTranslateValue, setSecondTranslateValue] = useState(null)
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false)
    const [saveAllLoading, setSaveAllLoading] = useState<boolean>(false)
    const [saveCardBtnItems, setSaveCardBtnItems] = useState([
        {
            key: '1',
            label: 'Сохранить все',
            disabled: true
        }
    ])

    useEffect(() => {
        if (wordItem.editMode && words.filter((w: WordDto) => w.editMode).length > 1) {
            const disabled = words.filter((w: WordDto) => w.editMode).some((w: WordDto) => !WordUtils.validateWord(w))
            setSaveCardBtnItems([{
                key: '1',
                label: 'Сохранить все',
                disabled: disabled
            }])
        }
    }, [words, wordItem]);


    const updateWordRedux = (wordListId: number, word: WordDto) =>
        dispatch(
            updateWord({
                wordListId: wordListId,
                word: word
            })
        )
    const createNewWordRedux = (wordListId: number, word: WordDto) =>
        dispatch(
            createNewWord({
                wordListId: wordListId,
                word: word
            })
        )

    /**
     * Перевести слово в режим редактирования
     * @param id слова
     */
    const editWorld = (id: ID) => {
        let wordsMap = words.map((w: WordDto) => {
            if (w.id === id) {
                return {
                    ...w,
                    editMode: true
                }
            }
            return w
        })
        setWordsRedux(wordsMap)
    }

    /**
     * Изменить слово
     * @param value - новое значение
     * @param id слова
     * @param lang - [engValue || rusValue] название поля для смены
     */
    const onChange = (value: any, id: ID, lang: string) => {
        let wordsMap = words.map((w: WordDto) => {
            if (w.id === id) {
                const updated = Object.assign({}, w)
                // @ts-ignore
                updated[lang] = value
                return updated
            }
            return w
        })
        setWordsRedux(wordsMap)
    }

    /**
     * Отменить режим редактирования слова
     * @param id слова
     */
    const cancelEdit = (id: string | number) => {
        if (WordUtils.isNewEntity(id)) {
            let wordsFilter = words.filter((w: WordDto) => w.id !== id)
            setWordsRedux(wordsFilter)
        } else {
            let wordsMap = words.map((w: WordDto) => {
                if (w.id === id) {
                    return {
                        ...w,
                        editMode: false,
                        engWord: w.prevEngWord,
                        rusWord: w.prevRusWord
                    }
                }
                return w
            })
            setWordsRedux(wordsMap)
        }
    }

    /**
     * Применить отредактированные значения
     * @param id слова
     */
    const applyEdit = (id: ID) => {
        let word = wordItem
        if (word.engWord && word.rusWord && word.engWord.length > 0 && word.rusWord.length > 0) {
            if (!WordUtils.isNewEntity(id)) {
                updateWordRedux(Number(cardList.id), word)
            } else {
                // @ts-ignore
                createNewWordRedux(cardList.id, word)
            }
        } else {
            Modal.error({
                title: 'Ошибка!',
                content: 'Заполните стороны карточки'
            })
        }
    }

    const deleteWordRedux = (wordListId: number, wordId: number) =>
        dispatch(
            deleteWord({
                wordListId: wordListId,
                wordId: wordId
            })
        )

    /**
     * Удалить слово Word
     * @param id - id слова
     */
    const delWord = (wid: number | string) => {
        // @ts-ignore
        deleteWordRedux(cardList.id, wid)
    }

    /**
     * Показать предупреждение перед удалением слова
     * @param id
     */
    const delWordWindow = () => {
        setShowConfirmModal(true)
    }

    const onChangeWord = (e: any, lang: string) => {
        const value = typeof e !== 'string' ? e.target.value : e
        const valid = WordUtils.validateWordString(value)
        if (lang === 'engWord') {
            setValidEng(valid)
        } else {
            setValidRus(valid)
        }
        onChange(value, id, lang)
    }

    const changeSecondTranslateValue = async (e: any) => {
        const value = typeof e !== 'string' ? e.target.value : e
        if (value) {
            if (sideTwoLang !== sideOneLang) {
                const resp = await translateCardText(value, sideOneLang || '', sideTwoLang || '')
                if (resp.success) {
                    setSecondTranslateValue(resp.data.text)
                }
            } else {
                setSecondTranslateValue(value)
            }

        } else {
            setSecondTranslateValue(null)
        }
    }
    const changeFirstTranslateValue = async (e: any) => {
        const value = typeof e !== 'string' ? e.target.value : e
        if (value) {
            if (sideTwoLang !== sideOneLang) {
                const resp = await translateCardText(value, sideTwoLang || '', sideOneLang || '')
                if (resp.success && isPremium) {
                    setFirstTranslateValue(resp.data.text)
                }
            } else {
                setFirstTranslateValue(value)
            }

        } else {
            setFirstTranslateValue(null)
        }
    }

    // @ts-ignore
    const handleSearch = async (value: string, lang: string, side: number) => {
        try {
            let {content} = await API_WORD.searchWord(value, lang);
            if (content && content.length > 0) {
                if (side === 1) {
                    // @ts-ignore
                    setSideOneOptions(mapLangToOptions(content));
                }
                if (side === 2) {
                    // @ts-ignore
                    setSideTwoOptions(mapLangToOptions(content));
                }

            }

        } catch (error) {
            setSideTwoOptions([]);
            setSideOneOptions([]);
        }
    };

    const handleSearchFirst = async (value?: string) => {
        if (!value || !sideOneLang) {
            setSideTwoOptions([]);
            setSideOneOptions([]);
            return;
        }
        await handleSearch(value, sideOneLang, 1)
    }

    const handleSearchSecond = async (value?: string) => {
        if (!value || !sideTwoLang) {
            setSideTwoOptions([]);
            setSideOneOptions([]);
            return;
        }
        await handleSearch(value, sideTwoLang, 2)
    }


    const debChangeSecond = useMemo(() => debounce(changeSecondTranslateValue, 1000), [sideOneLang, sideTwoLang])
    const debChangeFirst = useMemo(() => debounce(changeFirstTranslateValue, 1000), [sideTwoLang, sideTwoLang])
    const debSearchFirst = useMemo(() => debounce(handleSearchFirst, 1000), [sideTwoLang, sideTwoLang])
    const debSearchSecond = useMemo(() => debounce(handleSearchSecond, 1000), [sideTwoLang, sideTwoLang])
    /**
     * Отменить изменения слова
     */
    const cancelChanges = () => {
        setValidEng(true)
        setValidRus(true)
        cancelEdit(id)
    }


    const closeConfirmModal = () => {
        setShowConfirmModal(false)
    }

    /**
     * Сохранить сразу несколько слов
     */
    const saveAllWords = () => {
        setSaveAllLoading(true)
        const aWordList = cardList
        const wlId = aWordList.id
        const wordsFilter = words.filter((w: WordDto) => w.editMode)
        const wordsToSave = wordsFilter.filter((w: WordDto) => WordUtils.isNewEntity(w.id))
        const wordsToUpdate = wordsFilter.filter((w: WordDto) => !WordUtils.isNewEntity(w.id))
        API_WORD.saveWords(Number(aWordList.id), wordsToSave, wordsToUpdate).then(() => {
            setWordsRedux(words.filter((w: WordDto) => w.listId !== wlId))
            loadWords(Number(wlId))
        }).finally(() => {
            setSaveAllLoading(false)
        })
    }

    const onMenuClick = (e: any) => {
        if (e.key === '1') {
            saveAllWords();
        }
    }

    const mapLangToOptions = (content: Array<string>) => {
        return content.map((item: string) => {
            return {value: item}
        })
    }


    return (
        <div style={{width: '100%', minHeight: 'fit-content'}}> {/* Обертка */}
            <Card
                bordered
                className={editMode ? 'card-editing' : ''}
                style={{
                    borderRadius: 12,
                    marginBottom: 16,
                    height: 'auto',
                    minHeight: 'fit-content',
                    maxHeight: 'none',
                    overflow: 'visible'
                }}
            >
                <div

                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        height: 'auto',
                        minHeight: 'fit-content'
                    }}
                >
                    <Flex gap={24} vertical={true}>
                        {editMode && <EditOutlined style={{color: 'var(--color-primary)', marginLeft: 8}}/>}
                        <div>
                            <AutoComplete
                                maxLength={250}
                                options={sideOneOptions}
                                value={engWord}
                                className={!editMode ? 'no-focus' : ''}
                                open={editMode && isOpenSideOne}
                                onFocus={() => setIsOpenSideOne(true)}
                                onBlur={() => setIsOpenSideOne(false)}
                                onSelect={() => setIsOpenSideOne(false)}
                                placement="topLeft"
                                onChange={(e) => {
                                    if (editMode) {
                                        onChangeWord(e, 'engWord')
                                        debChangeSecond(e)
                                        debSearchFirst(e)
                                    }
                                }}
                                style={{width: "100%"}}
                            >
                                <Input.TextArea
                                    size={'large'}
                                    autoSize={{minRows: 2, maxRows: 20}}
                                    placeholder="Сторона 1"
                                    style={{
                                        borderColor: '#9154DE',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        resize: 'vertical'
                                    }}
                                />
                            </AutoComplete>
                            {editMode && firstTranslateValue && <TruncatedTag
                                text={firstTranslateValue}
                                color={'purple'} onClick={
                                () => {
                                    onChangeWord(firstTranslateValue, 'engWord')
                                    setFirstTranslateValue(null)
                                }
                            }/>}
                        </div>
                        <div>
                            <AutoComplete
                                options={sideTwoOptions}
                                value={rusWord}
                                className={!editMode ? 'no-focus' : ''}
                                open={editMode && isOpenSideTwo}
                                onFocus={() => setIsOpenSideTwo(true)}
                                onBlur={() => setIsOpenSideTwo(false)}
                                onSelect={() => setIsOpenSideTwo(false)}
                                placement="topLeft"
                                onChange={(e) => {
                                    if (editMode) {
                                        onChangeWord(e, 'rusWord')
                                        debChangeFirst(e)
                                        debSearchSecond(e)
                                    }
                                }}
                                style={{width: "100%"}}
                            >
                                <Input.TextArea
                                    size={'large'}
                                    autoSize={{minRows: 2, maxRows: 20}}
                                    placeholder="Сторона 2"
                                    style={{
                                        borderColor: '#3F96FE',
                                        width: '100%',
                                        boxSizing: 'border-box',
                                        resize: 'vertical'
                                    }}
                                />
                            </AutoComplete>
                            {editMode && secondTranslateValue && <TruncatedTag
                                onClick={
                                    () => {
                                        onChangeWord(secondTranslateValue, 'rusWord')
                                        setSecondTranslateValue(null)
                                    }
                                }
                                text={secondTranslateValue}
                                color={'blue'}/>}
                        </div>
                    </Flex>
                    {canEditWord && <Flex justify="flex-end" style={{marginTop: '8px'}}>
                        <Flex gap='4px' justify='flex-end'>
                            {!editMode && <>
                                <Button onClick={() => editWorld(id)}>Изменить</Button>
                                <Button loading={loading} danger type='primary'
                                        onClick={delWordWindow}>Удалить</Button>
                            </>}

                            {editMode && <>
                                <Dropdown.Button
                                    disabled={!engWord || engWord?.length < 0 || !rusWord || rusWord?.length < 0 || !(validRus && validEng)}
                                    loading={loading || saveAllLoading}
                                    type={'primary'}
                                    onClick={() => {
                                        applyEdit(id)
                                        setFirstTranslateValue(null)
                                        setSecondTranslateValue(null)
                                    }}
                                    menu={{items: saveCardBtnItems, onClick: onMenuClick}}>Сохранить</Dropdown.Button>
                                {editMode && <Button onClick={cancelChanges}>Отменить</Button>}
                            </>}
                        </Flex>
                    </Flex>}
                </div>
            </Card>
            <ConfirmModalDialog
                message={'Вы уверены, что хотите удалить карточку?'}
                decline={closeConfirmModal}
                confirm={() => delWord(id)}
                open={showConfirmModal}
            />
        </div>
    )

}

export default WordItemV2